import { Card, Title, Text, Flex, ProgressBar, Badge, Button } from '@tremor/react';
import type { PhiStatsResponse } from '../types';

interface PhiPanelProps {
  data: PhiStatsResponse | null;
  onResetStats?: () => void;
}

export function PhiPanel({ data, onResetStats }: PhiPanelProps) {
  if (!data) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Phi/vLLM Classifier</Title>
        <div className="mt-4 h-48 flex items-center justify-center">
          <Text className="text-gray-500">Loading...</Text>
        </div>
      </Card>
    );
  }

  const { limiter, vllm, lifetime, recent, config, classifiersUsingPhi } = data;

  // Health status
  const isUnavailable = !vllm.available;
  const isBackoff = !isUnavailable && vllm.queueDepth > (config?.queueBackoffThreshold ?? 50);

  // Queue depth bar (relative to backoff threshold)
  const backoffThreshold = config?.queueBackoffThreshold ?? 50;
  const queuePct = backoffThreshold > 0 ? Math.min((vllm.queueDepth / backoffThreshold) * 100, 100) : 0;

  return (
    <Card className="bg-gray-900 ring-gray-800 h-full flex flex-col">
      <Flex justifyContent="between" alignItems="center">
        <Title className="text-gray-100">Phi/vLLM</Title>
        {isUnavailable && (
          <Badge color="red" size="sm">Unavailable</Badge>
        )}
        {!isUnavailable && isBackoff && (
          <Badge color="amber" size="sm">Queue Backoff</Badge>
        )}
        {!isUnavailable && !isBackoff && (
          <Badge color="emerald" size="sm">Healthy</Badge>
        )}
      </Flex>

      <div className="mt-4 space-y-4 flex-1 pb-3">
        {/* vLLM Queue Depth */}
        <div>
          <Flex justifyContent="between" className="mb-1">
            <Text className="text-gray-400 text-sm">vLLM Queue Depth</Text>
            <Text className="text-gray-200 font-medium">
              {vllm.queueDepth} / {backoffThreshold}
            </Text>
          </Flex>
          <ProgressBar
            value={queuePct}
            color={isUnavailable ? 'gray' : isBackoff ? 'amber' : 'emerald'}
            className="h-2"
          />
        </div>

        {/* Limiter State */}
        <div className="grid grid-cols-3 gap-2">
          <StatBox label="Running" value={limiter.running} />
          <StatBox label="Queued" value={limiter.queued} warn={limiter.queued > 20} />
          <StatBox label="Max Conc." value={limiter.maxConcurrent} />
        </div>

        {/* vLLM Metrics */}
        {vllm.available && (
          <div className="pt-2 border-t border-gray-800">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text className="text-gray-500 text-xs">GPU KV Cache</Text>
                <Text className={`font-medium ${
                  vllm.gpuKvCacheUsage !== null && vllm.gpuKvCacheUsage > 0.9
                    ? 'text-amber-400'
                    : 'text-gray-300'
                }`}>
                  {vllm.gpuKvCacheUsage !== null
                    ? `${(vllm.gpuKvCacheUsage * 100).toFixed(1)}%`
                    : '--'}
                </Text>
              </div>
              <div>
                <Text className="text-gray-500 text-xs">Token Throughput</Text>
                <Text className="text-gray-300 font-medium">
                  {vllm.tokenThroughput !== null
                    ? `${vllm.tokenThroughput.toFixed(0)} tok/s`
                    : '--'}
                </Text>
              </div>
            </div>
          </div>
        )}

        {/* Performance Stats */}
        <div className="pt-2 border-t border-gray-800">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Text className="text-gray-500 text-xs">Success Rate</Text>
              <Text className={`font-medium ${
                parseFloat(lifetime.successRate) >= 99 ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {lifetime.successRate}
              </Text>
            </div>
            <div>
              <Text className="text-gray-500 text-xs">Avg Latency</Text>
              <Text className="text-gray-300 font-medium">
                {lifetime.avgDurationMs ? `${lifetime.avgDurationMs}ms` : '--'}
              </Text>
            </div>
          </div>
        </div>

        {/* Lifetime Stats */}
        <div className="pt-2 border-t border-gray-800">
          <Text className="text-gray-500 text-xs mb-2">Lifetime</Text>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Flex justifyContent="between">
              <span className="text-gray-500">Requests</span>
              <span className="text-gray-400">{lifetime.totalRequests.toLocaleString()}</span>
            </Flex>
            <Flex justifyContent="between">
              <span className="text-gray-500">Errors</span>
              <span className={lifetime.totalErrors > 0 ? 'text-red-400' : 'text-gray-400'}>
                {lifetime.totalErrors}
              </span>
            </Flex>
            <Flex justifyContent="between">
              <span className="text-gray-500">Effective RPM</span>
              <span className="text-gray-400">{recent.effectiveRpm ?? 0}</span>
            </Flex>
            <Flex justifyContent="between">
              <span className="text-gray-500">Rate Limits</span>
              <span className={recent.rateLimitCount > 0 ? 'text-red-400' : 'text-gray-400'}>
                {recent.rateLimitCount}
              </span>
            </Flex>
          </div>
        </div>

        {/* Concurrency Config */}
        {config && (
          <div className="pt-2 border-t border-gray-800">
            <Text className="text-gray-500 text-xs mb-2">Dynamic Concurrency</Text>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Flex justifyContent="between">
                <span className="text-gray-500">Current</span>
                <span className="text-gray-400">{limiter.maxConcurrent}</span>
              </Flex>
              <Flex justifyContent="between">
                <span className="text-gray-500">Range</span>
                <span className="text-gray-400">{config.minConcurrent}–{config.maxConcurrent}</span>
              </Flex>
              <Flex justifyContent="between">
                <span className="text-gray-500">Backoff @</span>
                <span className="text-gray-400">{config.queueBackoffThreshold} queued</span>
              </Flex>
              <Flex justifyContent="between">
                <span className="text-gray-500">Scale-up @</span>
                <span className="text-gray-400">{config.queueScaleupThreshold} queued</span>
              </Flex>
            </div>
          </div>
        )}

        {/* Backend Assignments */}
        {classifiersUsingPhi && classifiersUsingPhi.length > 0 && (
          <div className="pt-2 border-t border-gray-800">
            <Text className="text-gray-500 text-xs mb-1">Classifiers Using Phi</Text>
            <div className="flex flex-wrap gap-1">
              {classifiersUsingPhi.map((name) => (
                <Badge key={name} color="blue" size="xs">{name}</Badge>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Reset Button */}
      {onResetStats && (
        <div className="pt-3 border-t border-gray-800 mt-auto">
          <Button
            size="xs"
            variant="secondary"
            className="w-full bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-gray-300"
            onClick={onResetStats}
          >
            Reset Stats
          </Button>
        </div>
      )}
    </Card>
  );
}

interface StatBoxProps {
  label: string;
  value: number | string;
  warn?: boolean;
}

function StatBox({ label, value, warn }: StatBoxProps) {
  return (
    <div className="bg-gray-800/50 rounded p-2 text-center">
      <Text className="text-gray-500 text-xs">{label}</Text>
      <Text className={`font-semibold ${warn ? 'text-amber-400' : 'text-gray-200'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Text>
    </div>
  );
}
